import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'font-medium border-0 m-0 p-0 no-underline inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-black text-white hover:bg-black/90',
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        'primary-inverse':
          'bg-primary-foreground text-primary hover:bg-primary-foreground/90',
        success: 'bg-success text-white hover:bg-success/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        black:
          'bg-black text-white hover:bg-black/90 focus-visible:ring-destructive/20',
        outline:
          'border bg-background shadow-xs hover:bg-accent text-foreground hover:text-accent-foreground',
        'outline-primary':
          'bg-white border border-primary text-primary hover:bg-primary hover:text-white',
        'outline-success':
          'bg-white border text-success border-success hover:bg-success hover:text-white focus-visible:ring-destructive/20',
        'outline-warning':
          'bg-white border border-warning-dark text-warning-dark hover:bg-warning-dark hover:text-white',
        'outline-destructive':
          'bg-white text-destructive border border-destructive hover:bg-destructive hover:text-white focus-visible:ring-destructive/20',
        'outline-ghost': 'hover:bg-accent hover:text-accent-foreground border',
        'outline-secondary':
          'bg-white border text-gray-600 border-gray-500 hover:bg-black hover:text-white focus-visible:ring-destructive/20',
        'outline-white':
          'bg-transparent text-white border border-white hover:bg-white hover:text-black focus-visible:ring-destructive/20',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground text-foreground-dark',
        'ghost-primary': 'hover:bg-blue-50 hover:text-primary text-primary',
        'glass-white':
          'bg-white/10 hover:bg-white/20 text-white border-1 border-white/20',
        link: 'bg-transparent text-primary underline-offset-4 hover:underline',
        plain:
          'bg-transparent underline-offset-4 hover:underline text-primary p-0! text-size-inherit! h-auto!',
        warning:
          'bg-warning-dark text-warning-foreground hover:bg-warning-dark/90',
        'gradient-primary':
          'bg-linear-to-r from-[#81d2c7] to-primary text-primary-foreground hover:from-[#81d2c7]/90 hover:to-primary/90',
      },
      size: {
        default: 'h-9 px-4 py-2 text-sm has-[>svg]:px-3',
        xs: 'h-6 gap-1 rounded-sm px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*=size-])]:size-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-[13px]',
        lg: 'h-10 rounded-md px-6 text-sm has-[>svg]:px-4',
        xl: 'h-12 rounded-xl px-6 text-sm has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': 'size-6 rounded-md [&_svg:not([class*=size-])]:size-3',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
        link: 'items-baseline',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'primary',
  size = 'default',
  asChild = false,
  type = 'button',
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      type={type}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
